/******************************************************************************
 *** PreViewFile ** 첨부파일 미리보기
*******************************************************************************/
import { useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';

interface Props {
    blobUrl: string;
    fileName: string;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
const ROTATION_STEP = 90;

const PreViewFile = ( {blobUrl, fileName}: Props ) => {
    const getFileExtension = (name: string): string => {
        const lastDot = name.lastIndexOf('.');
        return lastDot >= 0 ? name.substring(lastDot + 1).toLowerCase() : '';
    };

    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const previewAreaRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const dragStartRef = useRef<{ pointerId: number; x: number; y: number; panX: number; panY: number } | null>(null);

    const fileExt = getFileExtension(fileName);
    const isPdf = fileExt === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileExt);

    const handleZoom = (delta: number) => {
        setZoom((prev) => {
            const next = Number((prev + delta).toFixed(2));
            const boundedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
            if (boundedZoom <= 1) setPan({ x: 0, y: 0 });
            return boundedZoom;
        });
    };

    const handleRotate = (delta: number) => {
        setRotation((prev) => (prev + delta + 360) % 360);
    };

    const handleImagePointerDown = (event: ReactPointerEvent<HTMLImageElement>) => {
        if (zoom <= 1) return;

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragStartRef.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        setIsDragging(true);
    };

    const handleImagePointerMove = (event: ReactPointerEvent<HTMLImageElement>) => {
        const dragStart = dragStartRef.current;
        const previewArea = previewAreaRef.current;
        const image = imageRef.current;
        if (!dragStart || dragStart.pointerId !== event.pointerId || !previewArea || !image) return;

        const radians = (rotation * Math.PI) / 180;
        const scaledWidth = (Math.abs(image.offsetWidth * Math.cos(radians)) + Math.abs(image.offsetHeight * Math.sin(radians))) * zoom;
        const scaledHeight = (Math.abs(image.offsetHeight * Math.cos(radians)) + Math.abs(image.offsetWidth * Math.sin(radians))) * zoom;
        const maxPanX = Math.max(0, (scaledWidth - previewArea.clientWidth) / 2);
        const maxPanY = Math.max(0, (scaledHeight - previewArea.clientHeight) / 2);
        const nextX = dragStart.panX + event.clientX - dragStart.x;
        const nextY = dragStart.panY + event.clientY - dragStart.y;

        setPan({
            x: Math.max(-maxPanX, Math.min(maxPanX, nextX)),
            y: Math.max(-maxPanY, Math.min(maxPanY, nextY)),
        });
    };

    const handleImagePointerEnd = (event: ReactPointerEvent<HTMLImageElement>) => {
        if (dragStartRef.current?.pointerId !== event.pointerId) return;

        dragStartRef.current = null;
        setIsDragging(false);
    };

    const resetImage = () => {
        setZoom(1);
        setRotation(0);
        setPan({ x: 0, y: 0 });
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: 'min(900px, calc(100vw - 80px))',
            height: 'min(650px, calc(100vh - 180px))',
            maxWidth: '100%',
            boxSizing: 'border-box',
        }}>
            <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                <span className="fs14 fm-b" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName}</span>
                {isImage && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={groupStyle}>
                            <button type="button" onClick={() => handleZoom(-ZOOM_STEP)} style={zoomOutButtonStyle} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.boxShadow = 'none'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.boxShadow = 'none'; }} onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }} onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>-</button>
                            <button type="button" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={zoomResetButtonStyle} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = 'none'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = 'none'; }} onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }} onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>100%</button>
                            <button type="button" onClick={() => handleZoom(ZOOM_STEP)} style={zoomInButtonStyle} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.boxShadow = 'none'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#86efac'; e.currentTarget.style.boxShadow = 'none'; }} onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }} onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>+</button>
                        </div>
                        <div style={groupStyle}>
                            <button type="button" onClick={() => handleRotate(-ROTATION_STEP)} style={rotateButtonStyle} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#6b7280'; e.currentTarget.style.boxShadow = 'none'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }} onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }} onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>↺</button>
                            <button type="button" onClick={() => handleRotate(ROTATION_STEP)} style={rotateButtonStyle} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#6b7280'; e.currentTarget.style.boxShadow = 'none'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = 'none'; }} onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }} onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>↻</button>
                            <button type="button" onClick={resetImage} style={resetButtonStyle} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = '#4b5563'; e.currentTarget.style.boxShadow = 'none'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.boxShadow = 'none'; }} onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }} onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>초기화</button>
                        </div>
                    </div>
                )}
            </div>
            {blobUrl ? (
                <div style={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    touchAction: 'none',
                }} ref={previewAreaRef}>
                    {isPdf ? (
                        <embed
                            src={blobUrl}
                            type="application/pdf"
                            width="100%"
                            height="100%"
                        />
                    ) : isImage ? (
                        <img
                            ref={imageRef}
                            src={blobUrl}
                            alt={fileName}
                            draggable={false}
                            onPointerDown={handleImagePointerDown}
                            onPointerMove={handleImagePointerMove}
                            onPointerUp={handleImagePointerEnd}
                            onPointerCancel={handleImagePointerEnd}
                            style={{
                                display: 'block',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                                transformOrigin: 'center center',
                                transition: 'transform 0.2s ease',
                                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                                userSelect: 'none',
                            }}
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <p>미리보기를 지원하지 않는 파일 형식입니다.</p>
                            <p>파일명: {fileName}</p>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <p>파일 로딩 중...</p>
                </div>
            )}
        </div>
    )
}

const groupStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    background: '#f9fafb',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
};

const baseButtonStyle: CSSProperties = {
    minWidth: '36px',
    height: '30px',
    padding: '0 10px',
    border: '1px solid transparent',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    transition: 'all 0.2s ease',
};

const zoomOutButtonStyle: CSSProperties = {
    ...baseButtonStyle,
    background: '#fee2e2',
    color: '#b91c1c',
    borderColor: '#fca5a5',
};

const zoomInButtonStyle: CSSProperties = {
    ...baseButtonStyle,
    background: '#dcfce7',
    color: '#166534',
    borderColor: '#86efac',
};

const zoomResetButtonStyle: CSSProperties = {
    ...baseButtonStyle,
    background: '#dbeafe',
    color: '#1e3a8a',
    borderColor: '#93c5fd',
};

const rotateButtonStyle: CSSProperties = {
    ...baseButtonStyle,
    background: '#f3f4f6',
    color: '#374151',
    borderColor: '#d1d5db',
};

const resetButtonStyle: CSSProperties = {
    ...baseButtonStyle,
    background: '#111827',
    color: '#fff',
    borderColor: '#374151',
};

export default PreViewFile;